import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
  Autocomplete,
  Tooltip,
  Alert,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import api from '../../../api';
import { sanitizeStripeCheckoutUrl } from '../../../utils/stripeCheckoutUrl';
import { toast } from 'react-toastify';

/** `past_paper` / `papers` both purchase `Paper` rows; API uses `paper_scope` to list the right catalogue. */
const RESOURCE_TYPES = [
  { value: 'past_paper', label: 'Past paper' },
  { value: 'papers', label: 'Papers' },
  { value: 'course', label: 'Course' },
  { value: 'mock_exam', label: 'Mock exam' },
];

function toApiResourceType(value) {
  if (value === 'past_paper' || value === 'papers') return 'paper';
  return value;
}

/**
 * Admin: send Stripe payment link to parent and/or record bank-transfer receipt for course, mock, past paper, or papers.
 */
const StudentPurchaseResourcesDialog = ({ open, onClose, student }) => {
  const [resourceType, setResourceType] = useState('past_paper');
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [coursePricePlans, setCoursePricePlans] = useState([]);
  const [loadingCoursePlans, setLoadingCoursePlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  /** After successful Stripe session creation — show URL to admin (email uses same link when send_email is true). */
  const [createdLinkUrl, setCreatedLinkUrl] = useState(null);
  const [createdLinkEmailInfo, setCreatedLinkEmailInfo] = useState(null);
  const [submittingLink, setSubmittingLink] = useState(false);
  const [submittingOffline, setSubmittingOffline] = useState(false);
  const [receiptReference, setReceiptReference] = useState('');
  const [amount, setAmount] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [bankReceiptFile, setBankReceiptFile] = useState(null);
  const bankReceiptInputRef = useRef(null);

  const resetForm = () => {
    setResourceType('past_paper');
    setOptions([]);
    setSelectedResource(null);
    setCoursePricePlans([]);
    setSelectedPlanId('');
    setSendEmail(true);
    setCreatedLinkUrl(null);
    setCreatedLinkEmailInfo(null);
    setReceiptReference('');
    setAmount('');
    setAdminNotes('');
    setBankReceiptFile(null);
    if (bankReceiptInputRef.current) {
      bankReceiptInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingOptions(true);
      setSelectedResource(null);
      try {
        let res;
        const purchaseParams = { per_page: 200, page: 1, for_student_purchase: 1 };
        if (resourceType === 'course') {
          res = await api.get('admin/assign/course', { params: purchaseParams });
        } else if (resourceType === 'mock_exam') {
          res = await api.get('admin/mock-exam', { params: purchaseParams });
        } else if (resourceType === 'past_paper' || resourceType === 'papers') {
          const paperScope = resourceType === 'past_paper' ? 'past_papers' : 'papers';
          res = await api.get('admin/paper', {
            params: { ...purchaseParams, paper_scope: paperScope },
          });
        } else {
          res = await api.get('admin/paper', { params: purchaseParams });
        }
        if (cancelled) return;
        const paginated = res.data?.data;
        const rows = Array.isArray(paginated?.data)
          ? paginated.data
          : Array.isArray(paginated)
            ? paginated
            : [];
        setOptions(rows);
      } catch (e) {
        if (!cancelled) {
          toast.error('Failed to load resources');
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, resourceType]);

  useEffect(() => {
    if (!open || resourceType !== 'course' || !selectedResource?.id) {
      setCoursePricePlans([]);
      setSelectedPlanId('');
      return;
    }
    let cancelled = false;
    const loadPlans = async () => {
      setLoadingCoursePlans(true);
      setSelectedPlanId('');
      try {
        const res = await api.get(`admin/assign/course/${selectedResource.id}`);
        if (cancelled) return;
        const plans = res.data?.data?.price_plans;
        const list = Array.isArray(plans) ? plans : [];
        setCoursePricePlans(list);
        if (list.length === 1) {
          setSelectedPlanId(String(list[0].id));
        }
      } catch {
        if (!cancelled) {
          setCoursePricePlans([]);
          toast.error('Could not load course plans');
        }
      } finally {
        if (!cancelled) setLoadingCoursePlans(false);
      }
    };
    loadPlans();
    return () => { cancelled = true; };
  }, [open, resourceType, selectedResource?.id]);

  const planLabel = useCallback((plan) => {
    if (!plan) return '';
    const name = plan.plan_name || plan.mode || 'Plan';
    const mode = plan.plan_name && plan.mode ? ` · ${plan.mode}` : '';
    const loc = plan.plan_location ? ` · ${plan.plan_location}` : '';
    const cur = plan.currency || '£';
    const amt = plan.amount != null ? ` · ${cur}${Number(plan.amount).toFixed(2)}` : '';
    const reg = plan.registration_fee != null ? ` · Reg ${cur}${Number(plan.registration_fee).toFixed(2)}` : '';
    const seats = plan.available_seats != null ? ` · ${plan.available_seats} seats left` : '';
    return `${name}${mode}${loc}${amt}${reg}${seats}`;
  }, []);

  const getOptionLabel = useCallback((opt) => {
    if (!opt) return '';
    return opt.name || opt.title || `#${opt.id}`;
  }, []);

  const courseNeedsPlanChoice = useMemo(
    () => resourceType === 'course' && coursePricePlans.length > 1,
    [resourceType, coursePricePlans.length]
  );

  const offlinePaymentDisabled = useMemo(
    () =>
      submittingOffline
      || !selectedResource
      || !bankReceiptFile
      || (resourceType === 'course' && loadingCoursePlans)
      || (courseNeedsPlanChoice && !selectedPlanId),
    [
      submittingOffline,
      selectedResource,
      bankReceiptFile,
      resourceType,
      loadingCoursePlans,
      courseNeedsPlanChoice,
      selectedPlanId,
    ]
  );

  const offlinePaymentDisabledHint = useMemo(() => {
    if (submittingOffline) return '';
    if (!selectedResource) {
      return 'Choose a past paper, paper, course, or mock in the search field above — the receipt alone is not enough.';
    }
    if (!bankReceiptFile) return 'Upload a bank transfer receipt.';
    if (resourceType === 'course' && loadingCoursePlans) return 'Wait for course plans to finish loading.';
    if (courseNeedsPlanChoice && !selectedPlanId) return 'Select a course plan.';
    return '';
  }, [
    submittingOffline,
    selectedResource,
    bankReceiptFile,
    resourceType,
    loadingCoursePlans,
    courseNeedsPlanChoice,
    selectedPlanId,
  ]);

  const stripeLinkDisabled = useMemo(
    () =>
      submittingLink
      || !selectedResource
      || (resourceType === 'course' && loadingCoursePlans)
      || (courseNeedsPlanChoice && !selectedPlanId),
    [submittingLink, selectedResource, resourceType, loadingCoursePlans, courseNeedsPlanChoice, selectedPlanId]
  );

  /** Student list uses `id`; student detail API uses `student_detail_id`. */
  const studentRecordId = student?.id ?? student?.student_detail_id;

  const handleCreateLink = async () => {
    if (!studentRecordId || !selectedResource?.id) {
      toast.error('Select a resource');
      return;
    }
    if (courseNeedsPlanChoice && !selectedPlanId) {
      toast.error('Select a course plan');
      return;
    }
    setSubmittingLink(true);
    try {
      const body = {
        resource_type: toApiResourceType(resourceType),
        resource_id: selectedResource.id,
        send_email: sendEmail,
      };
      if (resourceType === 'course' && selectedPlanId) {
        body.course_price_id = parseInt(selectedPlanId, 10);
      }
      const res = await api.post(`admin/student/${studentRecordId}/purchase/payment-link`, body);
      if (res.data.success) {
        const rawUrl = res.data.data?.checkout_url;
        const url = sanitizeStripeCheckoutUrl(rawUrl);
        if (rawUrl && url) {
          setCreatedLinkUrl(url);
          setCreatedLinkEmailInfo({
            sent: !!res.data.data?.email_sent,
            requested: sendEmail,
            email: res.data.data?.parent_email || student?.parent?.email || null,
            error: res.data.data?.email_error || null,
          });
          toast.success(res.data.message || 'Payment link created');
          try {
            await navigator.clipboard.writeText(url);
            toast.info('Link copied to clipboard');
          } catch {
            /* ignore */
          }
        } else if (rawUrl && !url) {
          toast.error('Invalid payment URL from server. Check API response and Stripe configuration.');
        } else {
          toast.success(res.data.message || 'No payment required — access granted.');
          onClose(true);
        }
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed';
      toast.error(msg);
    } finally {
      setSubmittingLink(false);
    }
  };

  const handleOffline = async () => {
    if (!studentRecordId || !selectedResource?.id) {
      toast.error('Select a resource');
      return;
    }
    if (!bankReceiptFile) {
      toast.error('Upload a bank transfer receipt (PDF or image)');
      return;
    }
    if (courseNeedsPlanChoice && !selectedPlanId) {
      toast.error('Select a course plan');
      return;
    }
    setSubmittingOffline(true);
    try {
      const fd = new FormData();
      fd.append('resource_type', toApiResourceType(resourceType));
      fd.append('resource_id', String(selectedResource.id));
      fd.append('receipt', bankReceiptFile);
      if (receiptReference.trim() !== '') {
        fd.append('receipt_reference', receiptReference.trim());
      }
      if (adminNotes.trim() !== '') {
        fd.append('admin_notes', adminNotes.trim());
      }
      if (amount.trim() !== '') {
        fd.append('amount', String(parseFloat(amount, 10)));
      }
      if (resourceType === 'course' && selectedPlanId) {
        fd.append('course_price_id', String(parseInt(selectedPlanId, 10)));
      }
      const res = await api.post(`admin/student/${studentRecordId}/purchase/offline`, fd);
      if (res.data.success) {
        toast.success(res.data.message || 'Purchase recorded');
        onClose(true);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed';
      toast.error(msg);
    } finally {
      setSubmittingOffline(false);
    }
  };

  if (!student) return null;

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Purchase resource for student</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {student.display_name
            || `${student.student?.first_name || student.student_user?.first_name || ''} ${student.student?.last_name || student.student_user?.last_name || ''}`.trim()}
          {student.parent?.email && (
            <> — Parent: <strong>{student.parent.email}</strong></>
          )}
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }} size="small">
          <InputLabel>Resource type</InputLabel>
          <Select
            value={resourceType}
            label="Resource type"
            onChange={(e) => setResourceType(e.target.value)}
          >
            {RESOURCE_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          options={options}
          loading={loadingOptions}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          value={selectedResource}
          onChange={(_, v) => {
            setSelectedResource(v);
            if (resourceType !== 'course') {
              setCoursePricePlans([]);
              setSelectedPlanId('');
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                resourceType === 'past_paper'
                  ? 'Past paper'
                  : resourceType === 'papers'
                    ? 'Papers'
                    : resourceType === 'mock_exam'
                      ? 'Mock exam'
                      : 'Course'
              }
              placeholder="Search / select"
              size="small"
              required
            />
          )}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mb: 1, display: 'block' }}>
          Required: pick the product to grant access to (not only the receipt below).
        </Typography>

        {resourceType === 'course' && selectedResource && (
          <Box sx={{ mt: 2 }}>
            {loadingCoursePlans ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">Loading course plans…</Typography>
              </Box>
            ) : coursePricePlans.length > 0 ? (
              <FormControl fullWidth size="small" required={coursePricePlans.length > 1}>
                <InputLabel>Course plan</InputLabel>
                <Select
                  label="Course plan"
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                >
                  {coursePricePlans.length > 1 && (
                    <MenuItem value="">
                      <em>Select a plan</em>
                    </MenuItem>
                  )}
                  {coursePricePlans.map((plan) => (
                    <MenuItem key={plan.id} value={String(plan.id)}>
                      {planLabel(plan)}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Choose the billing option for this family (online/in-person, billing period, and registration fee). This is used for both the payment link and offline payment.
                </Typography>
              </FormControl>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No per-plan prices on this course — the default / full-year registration fee from the course will be used.
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon fontSize="small" /> Stripe payment link
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Creates the same checkout parents get from billing. After payment, Stripe redirects to FRONTEND_URL from the API .env
          (parent site origin, not a private S3 URL) — wrong values cause an XML Access Denied page after checkout.
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />}
          label="Email payment link to parent"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, pl: 0.5 }}>
          When checked, the same link below is emailed to the parent address on file after the link is created.
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Button
            variant="contained"
            disabled={stripeLinkDisabled}
            onClick={handleCreateLink}
            startIcon={submittingLink ? <CircularProgress size={18} /> : <LinkIcon />}
          >
            {submittingLink ? 'Creating…' : 'Create payment link'}
          </Button>
        </Box>

        {createdLinkUrl ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Payment link
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              {createdLinkEmailInfo?.sent && createdLinkEmailInfo?.email
                ? `Email sent to ${createdLinkEmailInfo.email}.`
                : createdLinkEmailInfo?.error
                  ? createdLinkEmailInfo.error
                  : createdLinkEmailInfo?.requested
                    ? 'Email was requested — check the parent inbox (and spam).'
                    : 'Email was not sent — enable “Email payment link to parent” and create again, or copy the link below.'}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={createdLinkUrl}
              size="small"
              sx={{ mt: 1.5 }}
              InputProps={{ readOnly: true }}
              onFocus={(e) => e.target.select()}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(createdLinkUrl);
                    toast.info('Link copied');
                  } catch {
                    toast.error('Could not copy — select the text above');
                  }
                }}
              >
                Copy link
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open(createdLinkUrl, '_blank', 'noopener,noreferrer')}
              >
                Open in new tab
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setCreatedLinkUrl(null);
                  setCreatedLinkEmailInfo(null);
                  onClose(true);
                }}
              >
                Done
              </Button>
            </Box>
          </Alert>
        ) : null}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongIcon fontSize="small" /> Bank transfer / receipt
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Upload proof of bank transfer (required). Add a reference or notes if helpful.
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button variant="outlined" component="label" size="small">
            Choose receipt (PDF or image)
            <input
              ref={bankReceiptInputRef}
              type="file"
              hidden
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setBankReceiptFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          {bankReceiptFile && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 220 }} noWrap title={bankReceiptFile.name}>
                {bankReceiptFile.name}
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  setBankReceiptFile(null);
                  if (bankReceiptInputRef.current) bankReceiptInputRef.current.value = '';
                }}
              >
                Remove
              </Button>
            </>
          )}
        </Box>
        <TextField
          fullWidth
          size="small"
          label="Receipt / reference (optional)"
          sx={{ mb: 1 }}
          value={receiptReference}
          onChange={(e) => setReceiptReference(e.target.value)}
        />
        <TextField
          fullWidth
          size="small"
          label="Amount (optional)"
          type="number"
          sx={{ mb: 1 }}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <TextField
          fullWidth
          size="small"
          label="Admin notes (optional)"
          multiline
          minRows={2}
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
        />
        <Box sx={{ mt: 2 }}>
          <Tooltip
            title={offlinePaymentDisabled ? offlinePaymentDisabledHint : ''}
            disableHoverListener={!offlinePaymentDisabled}
          >
            <span>
              <Button
                variant="outlined"
                color="secondary"
                disabled={offlinePaymentDisabled}
                onClick={handleOffline}
                startIcon={submittingOffline ? <CircularProgress size={18} /> : <ReceiptLongIcon />}
              >
                {submittingOffline ? 'Saving…' : 'Record offline payment & grant access'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentPurchaseResourcesDialog;
